class GccProduct extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('[data-gcc-product-form]');
    this.variantData = this.parseVariantData();
    this.options = Array.from(this.querySelectorAll('[data-variant-picker] fieldset'));
    this.mediaItems = Array.from(this.querySelectorAll('[data-media-id]'));
    this.mediaStage = this.querySelector('[data-media-stage]');
    this.zoomDialog = this.querySelector('[data-media-zoom-dialog]');
    this.zoomImage = this.querySelector('[data-media-zoom-image]');
    this.currentVariant = this.getInitialVariant();

    this.onOptionChange = this.onOptionChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onQuantityInput = this.onQuantityInput.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onMediaKeydown = this.onMediaKeydown.bind(this);
    this.onMediaScroll = this.onMediaScroll.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
  }

  connectedCallback() {
    if (!this.form || !this.variantData.length) return;
    const idInput = this.form.querySelector('[data-variant-id]');
    if (idInput) idInput.name = 'id';

    this.addEventListener('change', this.onOptionChange);
    this.addEventListener('click', this.onClick);
    this.form.addEventListener('submit', this.onSubmit);
    this.form.querySelector('[data-quantity-input]')?.addEventListener('input', this.onQuantityInput);

    if (this.mediaStage) {
      this.mediaStage.addEventListener('scroll', this.onMediaScroll, { passive: true });
      this.mediaStage.addEventListener('keydown', this.onMediaKeydown);
    }
    this.zoomDialog?.addEventListener('click', (event) => {
      if (event.target === this.zoomDialog) this.zoomDialog.close();
    });

    this.updateOptionAvailability();
    this.setupProductVideos();
    this.updateProduct(this.currentVariant, false);
    this.setupStickyPurchaseVisibility();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onOptionChange);
    this.removeEventListener('click', this.onClick);
    this.form?.removeEventListener('submit', this.onSubmit);
    this.form?.querySelector('[data-quantity-input]')?.removeEventListener('input', this.onQuantityInput);
    this.mediaStage?.removeEventListener('scroll', this.onMediaScroll);
    this.mediaStage?.removeEventListener('keydown', this.onMediaKeydown);
    window.cancelAnimationFrame(this.mediaScrollFrame);
    this.footerObserver?.disconnect();
    this.productVideoObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  setupStickyPurchaseVisibility() {
    const stickyPurchase = this.querySelector('.product-main__cta-wrapper--sticky');
    const footer = document.querySelector('[data-footer]');
    if (!stickyPurchase || !footer || !('IntersectionObserver' in window)) return;

    this.footerObserver = new IntersectionObserver((entries) => {
      const footerIsVisible = entries.some((entry) => entry.isIntersecting);
      this.toggleAttribute('data-sticky-cta-suppressed', footerIsVisible);
    }, { rootMargin: '0px 0px 96px', threshold: 0.01 });
    this.footerObserver.observe(footer);
  }

  setupProductVideos() {
    this.productVideos = Array.from(this.querySelectorAll('[data-gcc-product-video]'));
    if (!this.productVideos.length) return;

    this.productVideos.forEach((video) => {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
    });
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    if (!('IntersectionObserver' in window)) return;
    this.productVideoObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        const selected = video.closest('[data-media-id]')?.classList.contains('is-selected');
        if (entry.isIntersecting && selected && document.visibilityState === 'visible'
          && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    }, { threshold: 0.35 });
    this.productVideos.forEach((video) => this.productVideoObserver.observe(video));
  }

  refreshProductVideos() {
    if (!this.productVideos?.length) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.productVideos.forEach((video) => {
      const selected = video.closest('[data-media-id]')?.classList.contains('is-selected');
      const rect = video.getBoundingClientRect();
      const visible = rect.bottom > 0 && rect.top < window.innerHeight;
      if (selected && visible && document.visibilityState === 'visible' && !reducedMotion) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }

  onVisibilityChange() {
    this.refreshProductVideos();
  }

  parseVariantData() {
    try {
      return JSON.parse(this.querySelector('[data-variant-data]')?.textContent || '[]');
    } catch (error) {
      console.error('Unable to read product variants.', error);
      return [];
    }
  }

  getInitialVariant() {
    const id = Number(new URL(window.location.href).searchParams.get('variant'));
    return this.variantData.find((variant) => variant.id === id)
      || this.variantData.find((variant) => variant.available)
      || this.variantData[0];
  }

  getSelectedOptions() {
    return this.options.map((fieldset) => fieldset.querySelector('input:checked')?.value);
  }

  onOptionChange(event) {
    if (event.target.matches('[data-quantity-input]')) {
      this.updateTotalPrice();
      return;
    }
    if (!event.target.matches('[data-option-value]')) return;

    let selectedOptions = this.getSelectedOptions();
    let variant = this.variantData.find((item) =>
      item.options.every((value, index) => value === selectedOptions[index])
    );

    if (!variant) {
      const changedIndex = Number(event.target.closest('[data-option-position]')?.dataset.optionPosition || 1) - 1;
      const candidates = this.variantData.filter((item) => {
        if (item.options[changedIndex] !== event.target.value) return false;
        return item.options.every((value, index) => index >= changedIndex || value === selectedOptions[index]);
      });
      variant = candidates.find((item) => item.available) || candidates[0];

      if (variant) {
        variant.options.forEach((value, index) => {
          const fieldset = this.options[index];
          const input = Array.from(fieldset?.querySelectorAll('[data-option-value]') || [])
            .find((candidate) => candidate.value === value);
          if (input) input.checked = true;
        });
        selectedOptions = this.getSelectedOptions();
      }
    }

    this.options.forEach((fieldset, index) => {
      const selected = fieldset.querySelector('input:checked');
      const output = this.querySelector(`[data-selected-option="${index + 1}"]`);
      if (selected && output) output.textContent = selected.value;
    });

    this.updateOptionAvailability();
    this.updateProduct(variant);
  }

  onQuantityInput() {
    this.updateTotalPrice();
    this.updateQuantityControls();
  }

  onMediaScroll() {
    window.cancelAnimationFrame(this.mediaScrollFrame);
    this.mediaScrollFrame = window.requestAnimationFrame(() => this.updateMediaCounter());
  }

  onMediaKeydown(event) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    if (event.key === 'Home') {
      this.focusMedia(Number(this.mediaItems[0]?.dataset.mediaId));
      return;
    }
    if (event.key === 'End') {
      this.focusMedia(Number(this.mediaItems[this.mediaItems.length - 1]?.dataset.mediaId));
      return;
    }
    this.focusAdjacentMedia(event.key === 'ArrowLeft' ? -1 : 1);
  }

  onClick(event) {
    const quantityButton = event.target.closest('[data-quantity-change]');
    if (quantityButton) {
      const input = this.querySelector('[data-quantity-input]');
      if (!input) return;
      const minimum = Number(input.min || 1);
      const maximum = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
      const next = Math.min(maximum, Math.max(minimum, Number(input.value || 1) + Number(quantityButton.dataset.quantityChange)));
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const thumbnail = event.target.closest('[data-media-thumbnail]');
    if (thumbnail) {
      this.focusMedia(Number(thumbnail.dataset.mediaThumbnail));
      return;
    }

    const previous = event.target.closest('[data-media-previous]');
    if (previous) {
      this.focusAdjacentMedia(-1);
      return;
    }

    const next = event.target.closest('[data-media-next]');
    if (next) {
      this.focusAdjacentMedia(1);
      return;
    }

    const zoomTrigger = event.target.closest('[data-media-zoom]');
    if (zoomTrigger && this.zoomDialog && this.zoomImage) {
      this.zoomImage.src = zoomTrigger.dataset.zoomSrc;
      this.zoomImage.alt = zoomTrigger.dataset.zoomAlt || '';
      if (typeof this.zoomDialog.showModal === 'function') {
        this.zoomDialog.showModal();
      }
      return;
    }

    if (event.target.closest('[data-media-zoom-close]')) {
      this.zoomDialog?.close();
    }
  }

  updateOptionAvailability() {
    const selectedOptions = this.getSelectedOptions();

    this.options.forEach((fieldset, optionIndex) => {
      fieldset.querySelectorAll('[data-option-value]').forEach((input) => {
        const candidates = this.variantData.filter((variant) => {
          if (variant.options[optionIndex] !== input.value) return false;
          return variant.options.every((value, index) => index >= optionIndex || value === selectedOptions[index]);
        });
        const hasAvailableCandidate = candidates.some((variant) => variant.available);
        input.disabled = candidates.length === 0 || !hasAvailableCandidate;
        const label = this.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        label?.toggleAttribute('data-unavailable', candidates.length > 0 && !hasAvailableCandidate);
        label?.setAttribute('aria-disabled', input.disabled ? 'true' : 'false');

        const exactCandidates = candidates.filter((variant) => variant.options.every((value, index) =>
          index === optionIndex || !selectedOptions[index] || value === selectedOptions[index]
        ));
        const cardVariant = exactCandidates.find((variant) => variant.available)
          || exactCandidates[0]
          || candidates.find((variant) => variant.available)
          || candidates[0];
        const price = label?.querySelector('[data-option-price]');
        const status = label?.querySelector('[data-option-status]');
        if (price) price.textContent = cardVariant?.priceFormatted || '';
        if (status) status.textContent = cardVariant?.status || (cardVariant?.available ? 'In Stock' : 'Out of Stock');
        if (label) {
          label.setAttribute('aria-label', [input.value, price?.textContent, status?.textContent].filter(Boolean).join(', '));
        }
      });
    });
  }

  updateProduct(variant, updateUrl = true) {
    this.currentVariant = variant;
    const idInput = this.form.querySelector('[data-variant-id]');
    const submit = this.form.querySelector('[data-add-to-cart]');
    const message = this.form.querySelector('[data-add-message]');
    const availability = this.form.querySelector('[data-product-availability]');
    const regularPrice = this.form.querySelector('[data-price-regular]');
    const comparePrice = this.form.querySelector('[data-price-compare]');
    const skuWrapper = this.form.querySelector('.gcc-product-form__sku');
    const sku = this.form.querySelector('[data-product-sku]');
    const quantityInput = this.form.querySelector('[data-quantity-input]');

    if (!variant) {
      if (idInput) idInput.value = '';
      if (submit) submit.disabled = true;
      if (message) message.textContent = 'Unavailable';
      if (availability) availability.textContent = 'Unavailable';
      return;
    }

    if (idInput) {
      idInput.value = variant.id;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (submit) submit.disabled = !variant.available;
    if (message) message.textContent = variant.available ? (variant.preorder ? 'Pre-Order' : 'Add to Cart') : 'Out of Stock';
    if (availability) availability.textContent = variant.status || (variant.available ? 'In Stock' : 'Out of Stock');
    if (regularPrice) regularPrice.textContent = variant.priceFormatted;

    if (comparePrice) {
      comparePrice.textContent = variant.compareAtPriceFormatted;
      comparePrice.hidden = !(variant.compareAtPrice > variant.price);
    }

    if (sku && skuWrapper) {
      sku.textContent = variant.sku || '';
      skuWrapper.hidden = !variant.sku;
    }
    this.form.querySelectorAll('[data-detail-sku]').forEach((item) => {
      item.textContent = variant.sku || 'Not assigned';
    });
    this.form.querySelectorAll('[data-detail-variant]').forEach((item) => {
      item.textContent = variant.options?.join(' / ') || '';
    });

    if (quantityInput) {
      const hasFiniteInventory = variant.inventoryManagement
        && variant.inventoryPolicy === 'deny'
        && variant.inventoryQuantity > 0;
      if (hasFiniteInventory) {
        quantityInput.max = variant.inventoryQuantity;
        quantityInput.value = Math.min(Number(quantityInput.value || 1), variant.inventoryQuantity);
      } else {
        quantityInput.removeAttribute('max');
      }
    }

    this.updateTotalPrice();
    this.updateQuantityControls();

    if (variant.featuredMediaId) this.focusMedia(variant.featuredMediaId);

    if (updateUrl && !window.Shopify?.designMode) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    this.dispatchEvent(new CustomEvent('variant:change', {
      bubbles: true,
      detail: { variant }
    }));
  }

  updateTotalPrice() {
    const totalPrice = this.form?.querySelector('[data-total-price]');
    if (!totalPrice || !this.currentVariant) return;
    const quantityInput = this.form.querySelector('[data-quantity-input]');
    const minimum = Number(quantityInput?.min || 1);
    const maximum = quantityInput?.max ? Number(quantityInput.max) : Number.POSITIVE_INFINITY;
    const quantity = Math.min(maximum, Math.max(minimum, Math.trunc(Number(quantityInput?.value || 1))));
    if (quantityInput) quantityInput.value = quantity;
    const totalInCents = this.currentVariant.price * quantity;

    const amount = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(totalInCents / 100);
    totalPrice.textContent = `AED ${amount}`;
  }

  updateQuantityControls() {
    const quantityInput = this.form?.querySelector('[data-quantity-input]');
    if (!quantityInput) return;
    const quantity = Number(quantityInput.value || 1);
    const minimum = Number(quantityInput.min || 1);
    const maximum = quantityInput.max ? Number(quantityInput.max) : Number.POSITIVE_INFINITY;
    const decrease = this.form.querySelector('[data-quantity-change="-1"]');
    const increase = this.form.querySelector('[data-quantity-change="1"]');
    if (decrease) decrease.disabled = quantity <= minimum;
    if (increase) increase.disabled = quantity >= maximum;
  }

  focusMedia(mediaId) {
    const item = this.querySelector(`[data-media-id="${mediaId}"]`);
    if (!item) return;

    this.mediaItems.forEach((media) => media.classList.toggle('is-selected', media === item));
    this.mediaItems.forEach((media) => {
      if (media !== item) media.querySelectorAll('video').forEach((video) => video.pause());
    });
    this.querySelectorAll('[data-media-thumbnail]').forEach((thumbnail) => {
      const active = Number(thumbnail.dataset.mediaThumbnail) === mediaId;
      thumbnail.classList.toggle('is-active', active);
      thumbnail.setAttribute('aria-current', active ? 'true' : 'false');
    });

    this.mediaStage?.scrollTo({
      left: item.offsetLeft,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
    window.requestAnimationFrame(() => this.refreshProductVideos());
  }

  focusAdjacentMedia(direction) {
    if (!this.mediaItems.length) return;
    const currentIndex = Math.max(0, this.mediaItems.findIndex((media) => media.classList.contains('is-selected')));
    const nextIndex = (currentIndex + direction + this.mediaItems.length) % this.mediaItems.length;
    this.focusMedia(Number(this.mediaItems[nextIndex].dataset.mediaId));
  }

  updateMediaCounter() {
    if (!this.mediaStage || !this.mediaItems.length) return;
    const index = this.mediaItems.reduce((closest, media, mediaIndex) => {
      const distance = Math.abs(media.offsetLeft - this.mediaStage.scrollLeft);
      return distance < closest.distance ? { index: mediaIndex, distance } : closest;
    }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;
    const output = this.querySelector('[data-media-current]');
    if (output) output.textContent = Math.min(index + 1, this.mediaItems.length);
    const activeMediaId = Number(this.mediaItems[index]?.dataset.mediaId);
    this.mediaItems.forEach((media, mediaIndex) => {
      media.classList.toggle('is-selected', mediaIndex === index);
    });
    this.querySelectorAll('[data-media-thumbnail]').forEach((thumbnail) => {
      const active = Number(thumbnail.dataset.mediaThumbnail) === activeMediaId;
      thumbnail.classList.toggle('is-active', active);
      thumbnail.setAttribute('aria-current', active ? 'true' : 'false');
    });
    this.refreshProductVideos();
  }

  onSubmit(event) {
    if (event.submitter && !event.submitter.matches('[data-add-to-cart]')) return;
    if (!this.currentVariant?.available) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    const submit = this.form.querySelector('[data-add-to-cart]');
    const error = this.form.querySelector('[data-product-error]');
    const quantityInput = this.form.querySelector('[data-quantity-input]');
    const maximum = quantityInput?.max ? Number(quantityInput.max) : Number.POSITIVE_INFINITY;
    const quantity = Math.min(maximum, Math.max(1, Math.trunc(Number(quantityInput?.value || 1))));
    const sellingPlan = this.form.elements.selling_plan?.value;
    const item = {
      id: this.currentVariant.id,
      quantity
    };
    if (sellingPlan) item.selling_plan = sellingPlan;

    if (submit) {
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
    }
    if (error) error.hidden = true;

    let completed = false;
    const finish = () => {
      if (completed) return;
      completed = true;
      if (submit) {
        submit.disabled = !this.currentVariant.available;
        submit.removeAttribute('aria-busy');
      }
    };

    const showError = (requestError) => {
      if (error) {
        error.textContent = requestError?.description
          || requestError?.message
          || 'This item could not be added to your cart.';
        error.hidden = false;
      }
      finish();
    };

    const addUrl = `${window.routes?.cart_add_url || '/cart/add'}.js`;
    const requestBody = {
      items: [item],
      sections: 'main-cart,header'
    };

    fetch(addUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(requestBody)
    })
      .then(async (response) => {
        const payload = await response.json();
        if (!response.ok || payload?.status >= 400) throw payload;

        document.dispatchEvent(new CustomEvent('updateCart', {
          detail: {
            cart: payload,
            options: { openCart: true }
          }
        }));
        finish();

        // If the exported mini-cart controller is unavailable, take the
        // customer to the real cart page instead of silently doing nothing.
        if (!customElements.get('section-cart') || !document.querySelector('section-cart')) {
          window.location.assign(window.routes?.cart_url || '/cart');
        }
      })
      .catch(showError);

    window.setTimeout(() => {
      if (!completed) showError({ message: 'The cart took too long to respond. Please try again.' });
    }, 12000);
  }
}

if (!customElements.get('gcc-product')) {
  customElements.define('gcc-product', GccProduct);
}

class GccPdpTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[data-pdp-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-pdp-panel]'));
    this.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-pdp-tab]');
      if (tab) this.selectTab(tab);
    });
    this.addEventListener('keydown', (event) => this.onKeydown(event));
    const selectedTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
    if (selectedTab) this.selectTab(selectedTab);
  }

  selectTab(tab, focus = false) {
    this.tabs.forEach((item) => {
      const active = item === tab;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-selected', active ? 'true' : 'false');
      item.tabIndex = active ? 0 : -1;
    });
    this.panels.forEach((panel) => {
      panel.hidden = panel.id !== tab.getAttribute('aria-controls');
    });
    if (focus) tab.focus();
  }

  onKeydown(event) {
    const currentIndex = this.tabs.indexOf(event.target);
    if (currentIndex < 0 || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    let nextIndex = currentIndex;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.tabs.length - 1;
    this.selectTab(this.tabs[nextIndex], true);
  }
}

if (!customElements.get('gcc-pdp-tabs')) {
  customElements.define('gcc-pdp-tabs', GccPdpTabs);
}

class GccProductRecommendations extends HTMLElement {
  connectedCallback() {
    const url = this.dataset.url;
    if (!url || this.dataset.loaded === 'true') return;
    const load = () => {
      if (this.dataset.loaded === 'true') return;
      this.dataset.loaded = 'true';
      fetch(url)
      .then((response) => response.text())
      .then((html) => {
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const incoming = documentFragment.querySelector('gcc-product-recommendations');
        if (incoming?.innerHTML.trim()) {
          this.innerHTML = incoming.innerHTML;
        } else {
          this.hidden = true;
        }
      })
      .catch(() => {
        this.hidden = true;
      });
    };

    if (!('IntersectionObserver' in window)) {
      load();
      return;
    }
    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.observer.disconnect();
      load();
    }, { rootMargin: '500px 0px' });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

if (!customElements.get('gcc-product-recommendations')) {
  customElements.define('gcc-product-recommendations', GccProductRecommendations);
}

class GccHighlightsCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-highlights-track]');
    this.slides = Array.from(this.querySelectorAll('[data-highlight-slide]'));
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-highlights-previous]')) this.move(-1);
      if (event.target.closest('[data-highlights-next]')) this.move(1);
    });
    this.track?.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      this.move(event.key === 'ArrowLeft' ? -1 : 1);
    });
  }

  move(direction) {
    if (!this.track || !this.slides.length) return;
    const current = this.slides.reduce((closest, slide, index) => {
      const distance = Math.abs(slide.offsetLeft - this.track.scrollLeft);
      return distance < closest.distance ? { index, distance } : closest;
    }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;
    const next = (current + direction + this.slides.length) % this.slides.length;
    this.track.scrollTo({
      left: this.slides[next].offsetLeft,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  }
}

if (!customElements.get('gcc-highlights-carousel')) {
  customElements.define('gcc-highlights-carousel', GccHighlightsCarousel);
}

class GccProductVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;
    this.playButton = this.querySelector('[data-play]');
    this.pauseButton = this.querySelector('[data-pause]');
    this.content = this.querySelector('[data-content]');
    this.playButton?.addEventListener('click', () => this.play());
    this.pauseButton?.addEventListener('click', () => this.pause());
    this.video.addEventListener('play', () => this.update(true));
    this.video.addEventListener('pause', () => this.update(false));
    this.video.muted = true;
    this.video.defaultMuted = true;
    this.video.playsInline = true;
    if (!this.video.preload) this.video.preload = 'metadata';

    if (!this.hasAttribute('data-autoplay-in-view')
      || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!('IntersectionObserver' in window)) {
      this.play();
      return;
    }
    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.play();
      else this.pause();
    }, { rootMargin: '160px 0px', threshold: 0.2 });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  play() {
    this.video?.play().catch(() => this.update(false));
  }

  pause() {
    this.video?.pause();
  }

  update(playing) {
    this.classList.toggle('is-playing', playing);
    this.content?.classList.toggle('fade-out', playing);
    this.pauseButton?.closest('.product-video__pause')?.classList.toggle('fade-out', !playing);
  }
}

if (!customElements.get('product-video')) {
  customElements.define('product-video', GccProductVideo);
}

class GccPdpSlideshow extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.swiper-slide'));
    if (!this.slides.length) return;
    this.index = 0;
    this.paused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.querySelector('.swiper-button-prev')?.addEventListener('click', () => this.show(this.index - 1));
    this.querySelector('.swiper-button-next')?.addEventListener('click', () => this.show(this.index + 1));
    this.querySelector('[data-pause]')?.addEventListener('click', () => {
      this.paused = !this.paused;
      this.classList.toggle('is-paused', this.paused);
      this.resetTimer();
    });
    this.show(0);
  }

  show(index) {
    this.index = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });
    this.resetTimer();
  }

  resetTimer() {
    window.clearTimeout(this.timer);
    if (!this.paused && this.slides.length > 1) {
      this.timer = window.setTimeout(() => this.show(this.index + 1), 6000);
    }
  }

  disconnectedCallback() {
    window.clearTimeout(this.timer);
  }
}

if (!customElements.get('pdp-slideshow')) {
  customElements.define('pdp-slideshow', GccPdpSlideshow);
}
